import { useNavigate } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';

import { createComment, deleteComment, updateComment } from 'api/Comment';

import { useCustomMutation } from 'lib/hooks/useCustomMutation';

export const useComment = (category, id) => {
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const updateActivities = (oldData, response, updateFn) => {
    if (!oldData) return oldData;

    const newPages = oldData?.pages?.map((page) => ({
      ...page,
      activities: updateFn(page.activities, response.comment)
    }));

    return {
      ...oldData,
      pages: newPages
    };
  };

  const handleSuccess = (response, updateFn) => {
    try {
      queryClient.setQueryData(['activities', category, id], (oldData) =>
        updateActivities(oldData, response, updateFn)
      );
    } catch (error) {
      console.error('Error updating queryClient activities', error);
    }
  };

  const { mutate: createCommentHandler, isLoading: isCreatingComment } = useCustomMutation({
    mutationFn: (params) => createComment(navigate, params),
    successMessage: 'Comment created successfully!',
    errorMessage: 'Failed to create comment. Please try again.',
    onSuccess: (response) =>
      handleSuccess(response, (activities, comment) => [...activities, comment])
  });

  const { mutate: deleteCommentHandler } = useCustomMutation({
    mutationFn: (params) => deleteComment(navigate, params),
    successMessage: 'Comment deleted successfully!',
    errorMessage: 'Failed to delete comment. Please try again.',
    onSuccess: (response) =>
      handleSuccess(response, (activities, comment) =>
        activities.filter((activity) => activity.id !== comment.id)
      )
  });

  const { mutate: updateCommentHandler } = useCustomMutation({
    mutationFn: (params) => updateComment(navigate, params),
    successMessage: 'Comment updated successfully!',
    errorMessage: 'Failed to update comment. Please try again.',
    onSuccess: (response) =>
      handleSuccess(response, (activities, comment) =>
        activities.map((activity) =>
          activity.type === 'comment' && activity.id === comment.id
            ? { ...activity, value: comment.value, updated_at: comment.updated_at }
            : activity
        )
      )
  });

  const onCommentPostHandler = async (value) => {
    await createCommentHandler({ refId: id, refTable: category, value });
  };

  const onCommentDeleteHandler = async (commentId) => {
    await deleteCommentHandler({ id: commentId });
  };

  const onCommentSaveHandler = async (comment) => {
    await updateCommentHandler({ id: comment.id, value: comment.value });
  };

  return {
    isCreatingComment,
    onCommentPostHandler,
    onCommentDeleteHandler,
    onCommentSaveHandler
  };
};
